import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import CrmFollowUps from "./CrmFollowUps.jsx";
import { localToday } from "../meetings/period.js";
import { colors, font, displayName } from "../utils/theme.js";
import { api } from "../api/client.js";
import TaskDetail from "../clients/TaskDetail.jsx";
import { inputStyle, primaryBtn, ghostBtn, STATUS_STYLE, STATUSES } from "../clients/ui.js";
import { bucketTasks, BUCKETS, BUCKET_LABEL } from "./buckets.js";
import TaskCreate from "./TaskCreate.jsx";
import { AssigneeSelect, DateChip, PrivateMark } from "../clients/InlineTaskFields.jsx";
import ImportTemplate from "../templates/ImportTemplate.jsx";

// "My Tasks" / Employees (increment 6). One person's whole workload — client work AND
// internal — from the shared tasks container, bucketed by time. Everyone can view anyone.
// Client-assigned tasks never appear here (the API query returns only assignee.type=user).
export default function EmployeesPage({ user, users, onOpen }) {
  const [personId, setPersonId] = useState(user?.id || "");
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openTask, setOpenTask] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [toast, setToast] = useState(null);

  const flash = (type, text) => { setToast({ type, text }); setTimeout(() => setToast((t) => (t && t.text === text ? null : t)), 5000); };

  const loadTasks = useCallback(async (uid) => {
    const res = await api.tasks.list({ userId: uid });
    // Defensive: the API already excludes client-assigned tasks; belt-and-suspenders here.
    setTasks((res?.items || []).filter((t) => t.assignee?.type === "user" && t.assignee.userId === uid));
  }, []);

  // CRM follow-ups owned by this person (CRM spec 2026-11-03). Best effort: a failure just
  // hides the list — it never blocks or errors the task view.
  const [crm, setCrm] = useState({ tracks: [], contacts: [] });
  useEffect(() => {
    let live = true;
    Promise.all([api.crmTracks.listAll({ ownerUserId: personId }), api.contacts.listAll()])
      .then(([tracks, contacts]) => { if (live) setCrm(tracks && contacts ? { tracks, contacts } : { tracks: [], contacts: [] }); })
      .catch(() => { if (live) setCrm({ tracks: [], contacts: [] }); });
    return () => { live = false; };
  }, [personId]);

  const loadRefs = useCallback(async () => {
    const [reg, obj, ops] = await Promise.all([api.clients.list(), api.objectives.list(), api.opsplanner.list()]);
    setClients(Array.isArray(reg) ? reg : []);
    setObjectives(obj?.items || []);
    setProjects(Array.isArray(ops) ? ops : []);
  }, []);

  useEffect(() => {
    let ok = true;
    (async () => {
      setLoading(true); setError(null);
      try { await Promise.all([loadTasks(personId), loadRefs()]); }
      catch (e) { if (ok) setError(e.message || "Could not load workload."); }
      finally { if (ok) setLoading(false); }
    })();
    return () => { ok = false; };
  }, [personId, loadTasks, loadRefs]);

  // Deep link from the daily email (#tasks&task=<id>): open that task once, even if it isn't
  // in the selected person's list (falls back to fetching it).
  const linkedRef = useRef(false);
  useEffect(() => {
    if (loading || linkedRef.current) return;
    const m = /[#&]task=([^&]+)/.exec(window.location.hash || "");
    if (!m) return;
    linkedRef.current = true;
    const id = decodeURIComponent(m[1]);
    const t = tasks.find((x) => x.id === id);
    if (t) setOpenTask(t);
    else api.tasks.get(id).then((x) => { if (x?.id) setOpenTask(x); }).catch(() => {});
  }, [loading, tasks]);

  const clientName = useCallback((id) => { if (!id) return "Internal"; const c = clients.find((x) => x.id === id); return c ? c.name : "Internal"; }, [clients]);
  const objectiveName = useCallback((id) => { const o = objectives.find((x) => x.id === id); return o ? o.name : null; }, [objectives]);
  const objectivePrivate = useCallback((id) => objectives.some((x) => x.id === id && x.private === true), [objectives]);
  const userName = useCallback((id) => { const u = (users || []).find((x) => x.id === id || x.email === id); return u ? displayName(u) : id; }, [users]);

  const buckets = useMemo(() => bucketTasks(tasks, Date.now()), [tasks]);

  const saveTask = async (id, fields) => {
    const prev = tasks;
    setTasks((t) => t.map((x) => (x.id === id ? { ...x, ...fields } : x)));
    const res = await api.tasks.update(id, fields);
    if (res.ok) { setTasks((t) => t.map((x) => (x.id === id ? res.data : x))); return true; }
    setTasks(prev); flash("error", "Couldn’t save task — reverted."); return false;
  };

  if (loading) return <div style={{ padding: 40, color: colors.textMuted }}>Loading workload…</div>;
  if (error) return (
    <div style={{ padding: 40 }}>
      <div style={{ color: colors.danger, marginBottom: 12 }}>Couldn’t load: {error}</div>
      <button onClick={() => setPersonId((p) => p)} style={primaryBtn}>Retry</button>
    </div>
  );

  const person = (users || []).find((u) => u.id === personId);
  const total = BUCKETS.reduce((n, k) => n + buckets[k].length, 0);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: colors.text, letterSpacing: -0.3 }}>My Tasks</h1>
        <span style={{ fontSize: 13, color: colors.textDim }}>Viewing</span>
        <select value={personId} onChange={(e) => setPersonId(e.target.value)} style={{ ...inputStyle, width: "auto", minWidth: 200 }}>
          {(users || []).map((u) => <option key={u.id} value={u.id}>{displayName(u)}{u.id === user?.id ? " (me)" : ""}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowImport(true)} style={ghostBtn}>Import template</button>
        <button onClick={() => setCreating(true)} style={primaryBtn}>+ New task</button>
      </div>

      {total > 0 && <Dashboard buckets={buckets} tasks={tasks} clientName={clientName} />}

      <CrmFollowUps tracks={crm.tracks} contacts={crm.contacts} clientName={(id) => clients.find((c) => c.id === id)?.name} today={localToday()} onOpen={onOpen} />

      {total === 0 && <div style={{ color: colors.textDim, fontSize: 14, padding: "20px 0" }}>{person ? displayName(person) : "This person"} has no tasks. Create one with “+ New task”.</div>}

      {BUCKETS.map((key) => {
        const items = buckets[key];
        if (items.length === 0) return null;
        const danger = key === "overdue";
        return (
          <div key={key} style={{ marginBottom: 22 }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: danger ? colors.danger : colors.textDim, marginBottom: 8 }}>
              {BUCKET_LABEL[key]} <span style={{ color: colors.textDim, fontWeight: 500 }}>· {items.length}</span>
            </div>
            <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: "hidden" }}>
              {items.map((t) => (
                <TaskRow key={t.id} t={t} clientName={clientName} objectiveName={objectiveName} objectivePrivate={objectivePrivate} userName={userName}
                  personId={personId} users={users} onOpen={() => setOpenTask(t)} onSave={(fields) => saveTask(t.id, fields)} completed={key === "recentlyCompleted"} />
              ))}
            </div>
          </div>
        );
      })}

      {openTask && (
        <TaskDetail task={openTask} users={users} clientId={openTask.clientId} clientName={clientName(openTask.clientId)}
          onSave={(fields) => saveTask(openTask.id, fields)} onClose={() => setOpenTask(null)} />
      )}
      {creating && (
        <TaskCreate users={users} clients={clients} projects={projects} objectives={objectives}
          defaultAssigneeId={personId}
          onClose={() => setCreating(false)}
          onCreated={async () => { setCreating(false); await Promise.all([loadTasks(personId), loadRefs()]); }} />
      )}
      {showImport && (
        <ImportTemplate clientId={null} projectId={null} contextLabel="As an internal objective"
          onClose={() => setShowImport(false)}
          onImported={async () => { setShowImport(false); await Promise.all([loadTasks(personId), loadRefs()]); }} />
      )}
      {toast && (
        <div onClick={() => setToast(null)} style={{ position: "fixed", bottom: 24, right: 24, zIndex: 1200, maxWidth: 360, padding: "12px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600, color: "#fff", background: toast.type === "error" ? colors.danger : colors.success, boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}>{toast.text}</div>
      )}
    </div>
  );
}

// Glanceable workload band above the buckets — counts + where the open work sits.
function Dashboard({ buckets, tasks, clientName }) {
  const open = buckets.overdue.length + buckets.thisWeek.length + buckets.nextWeek.length + buckets.later.length;
  // Active clients = distinct clients you currently have OPEN work with.
  const openClients = new Set();
  for (const t of tasks) if (t.status !== "Completed" && t.clientId) openClients.add(t.clientId);
  const activeClients = openClients.size;
  // Completed in the last 2 weeks, grouped by client (Internal = no-client work).
  const byClient = {};
  for (const t of buckets.recentlyCompleted) {
    const k = t.clientId || "__internal__";
    byClient[k] = (byClient[k] || 0) + 1;
  }
  const clientEntries = Object.entries(byClient)
    .map(([k, n]) => ({ name: k === "__internal__" ? "Internal" : clientName(k), n }))
    .sort((a, b) => b.n - a.n);
  const tiles = [
    { label: "Overdue", value: buckets.overdue.length, color: buckets.overdue.length ? colors.danger : colors.textDim },
    { label: "Due this week", value: buckets.thisWeek.length, color: colors.accent },
    { label: "Open total", value: open, color: colors.text },
    { label: "Completed · 14d", value: buckets.recentlyCompleted.length, color: colors.success },
    { label: "Active clients", value: activeClients, color: colors.text },
  ];
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: clientEntries.length ? 12 : 0 }}>
        {tiles.map((t) => (
          <div key={t.label} style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 10, padding: "10px 16px", minWidth: 110 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: colors.textDim, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 4 }}>{t.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: t.color, fontVariantNumeric: "tabular-nums" }}>{t.value}</div>
          </div>
        ))}
      </div>
      {clientEntries.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: colors.textDim, textTransform: "uppercase", letterSpacing: 1 }}>Completed last 2 weeks by client</span>
          {clientEntries.slice(0, 8).map((c) => (
            <span key={c.name} style={{ fontSize: 12, color: colors.textMuted, background: colors.surfaceAlt, border: `1px solid ${colors.border}`, borderRadius: 20, padding: "3px 10px" }}>{c.name} · <b style={{ color: colors.text }}>{c.n}</b></span>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskRow({ t, clientName, objectiveName, objectivePrivate, userName, personId, users, onOpen, onSave, completed }) {
  const st = STATUS_STYLE[t.status] || STATUS_STYLE["Not Started"];
  const overdue = !completed && t.dueDate && t.dueDate.slice(0, 10) < new Date().toISOString().slice(0, 10);
  const assignedByOther = t.createdBy && t.createdBy !== personId;
  const obj = objectiveName(t.objectiveId);
  return (
    <div onClick={onOpen} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderBottom: `1px solid ${colors.border}`, cursor: "pointer" }}
      onMouseEnter={(e) => (e.currentTarget.style.background = colors.surfaceAlt)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, color: colors.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}{t.clientId && (t.private || objectivePrivate(t.objectiveId)) && <> <PrivateMark objectiveLevel={!t.private} /></>}</div>
        <div style={{ fontSize: 11.5, color: colors.textDim, marginTop: 2 }}>
          {clientName(t.clientId)}{obj ? ` · ${obj}` : ""}{assignedByOther ? ` · assigned by ${userName(t.createdBy)}` : ""}
        </div>
      </div>
      <AssigneeSelect assignee={t.assignee} users={users} clientId={t.clientId} onChange={(a) => onSave({ assignee: a })} />
      <DateChip label="Start" value={t.startDate} onChange={(d) => onSave({ startDate: d })} />
      <DateChip label="Due" value={t.dueDate} danger={overdue} onChange={(d) => onSave({ dueDate: d })} />
      <select value={t.status} onClick={(e) => e.stopPropagation()} onChange={(e) => { e.stopPropagation(); onSave({ status: e.target.value }); }}
        style={{ fontSize: 10.5, fontWeight: 700, color: st.color, background: st.bg, border: `1px solid ${colors.border}`, borderRadius: 20, padding: "3px 8px", fontFamily: font, cursor: "pointer", outline: "none" }}>
        {STATUSES.map((s) => <option key={s} value={s} style={{ color: colors.text, background: colors.surface }}>{s}</option>)}
      </select>
      <span style={{ color: colors.textDim }}>›</span>
    </div>
  );
}
